import { kebabCase } from 'change-case';

import { BaseDomainType, NegotiationDomain, QueryParam } from '~/common/types';
import { joinPath } from '~/util/string/joinPath';

import { SupplierListTab } from './Analytics/Base/SupplierList/SupplierList';

export enum RouteIdPart {
  // main
  demand = 'demand',
  demandTabRequest = 'request',
  demandTabSuppliers = 'suppliers',
  demandTabAwarding = 'awarding',
  contractDemand = 'contract',
  create = 'create',
  new = 'new',
  createChat = 'create-chat',
  chat = 'chat',
  demandNegotiationCase = 'negotiation-case',
  negotiation = 'negotiation',
  negotiationCase = 'case',
  upload = 'upload',
  userProfile = 'profile',
  team = 'team',
  base = 'base',
  account = 'account',
  analytics = 'analytics',
  superset = 'superset',
  general = 'general',
  intake = 'intake',
  login = 'login',
  home = 'home',
  search = 'search',
  customData = 'custom-data',
  demo = 'sh',
  notifications = 'notifications',
  settings = 'settings',
  rule = 'rule',
  knowledgeRepository = 'knowledge-repository',
  teamManagement = 'team-management',
  support = 'support',
  smartLinks = 'smart-links',
  riskSustainability = 'risk-sustainability',
  internalTools = 'internal-tools',
  dataFoundation = 'data-foundation',
  savings = 'savings',
  log = 'log',
  auth = 'auth',
  teamsAuth = 'teams-auth',
  start = 'start',
  end = 'end',
  callback = 'callback',
  cockpit = 'cockpit',
  conversation = 'conversation',
  guided = 'guided',
  sourcingCase = 'sourcing-case',
  coverageAssistant = 'coverage-assistant',
  popupCallback = 'popup-callback',
}

export const RouteId = {
  base: `/${RouteIdPart.base}/*`,
  dataFoundationAll: `/${RouteIdPart.dataFoundation}/*`,
  demandAll: `/${RouteIdPart.demand}/*`,
  contractDemandAll: `/${RouteIdPart.contractDemand}/*`,
  analyticsAll: `/${RouteIdPart.analytics}/*`,
  negotiationAll: `/${RouteIdPart.negotiation}/*`,
  customDataAll: `/${RouteIdPart.customData}/*`,
  intakeAll: `/${RouteIdPart.intake}/*`,
  internalToolsAll: `/${RouteIdPart.internalTools}/*`,
  riskSustainabilityAll: `/${RouteIdPart.riskSustainability}/*`,
  settingsAll: `/${RouteIdPart.settings}/*`,
  ruleAll: `/${RouteIdPart.rule}/*`,
  team: `/${RouteIdPart.team}`,
  superset: `/${RouteIdPart.analytics}/${RouteIdPart.superset}`,
  analyticsGeneral: `/${RouteIdPart.analytics}/${RouteIdPart.general}`,
  analyticsBase: (domain = `:domain?`, queryId?: string): string => {
    return domain
      ? `/${RouteIdPart.analytics}/${RouteIdPart.base}/${kebabCase(domain)}${queryId ? `?q=${queryId}` : ``}`
      : `/${RouteIdPart.analytics}`;
  },
  analyticsSuperset: `/${RouteIdPart.analytics}/${RouteIdPart.superset}`,
  baseSupplier: (tab: SupplierListTab, queryId?: string): string => {
    return `/${RouteIdPart.analytics}/${RouteIdPart.base}/${kebabCase(BaseDomainType.SUPPLIER)}/${tab}${queryId ? `?q=${queryId}` : ``}`;
  },
  intakeNew: `/${RouteIdPart.intake}/${RouteIdPart.create}`,
  intakeCreate: `/${RouteIdPart.intake}/${RouteIdPart.create}`,
  intakeChatCreate: `/${RouteIdPart.intake}/${RouteIdPart.createChat}`,

  intakeChat: (id = ':id'): string => {
    return `/${RouteIdPart.intake}/${RouteIdPart.chat}/${id}`;
  },
  userProfile: (id = `:userId`): string => {
    return id ? `/${RouteIdPart.userProfile}/${id}` : `/${RouteIdPart.userProfile}`;
  },
  cmdi: (entityId = `:entityId?`): string => {
    return entityId ? `/${RouteIdPart.customData}/${kebabCase(entityId)}` : `/${RouteIdPart.customData}`;
  },
  commodityDetail: (id = ':id'): string =>
    `/${RouteIdPart.analytics}/${RouteIdPart.base}/${kebabCase(BaseDomainType.COMMODITY)}/${id}`,
  commodityGroupDetail: (id = ':id'): string =>
    `/${RouteIdPart.analytics}/${RouteIdPart.base}/${kebabCase(BaseDomainType.COMMODITY_GROUP)}/${id}`,
  companyDetail: (id = ':id'): string =>
    `/${RouteIdPart.analytics}/${RouteIdPart.base}/${kebabCase(BaseDomainType.COMPANY)}/${id}`,
  contractDetail: (id = ':id'): string =>
    `/${RouteIdPart.analytics}/${RouteIdPart.base}/${kebabCase(BaseDomainType.CONTRACT)}/${id}`,
  sourcingProjectDetail: (id = ':id'): string =>
    `/${RouteIdPart.analytics}/${RouteIdPart.base}/${kebabCase(BaseDomainType.SOURCING_PROJECT)}/${id}`,
  demand: `/${RouteIdPart.demand}`,
  demandCreate: `/${RouteIdPart.demand}/${RouteIdPart.create}`,
  contractDemand: `/${RouteIdPart.demand}/${RouteIdPart.contractDemand}`,
  contractDemandQuery: (encodedQuery: string): string =>
    `/${RouteIdPart.demand}/${RouteIdPart.contractDemand}?${QueryParam.ENCODED_QUERY}=${encodedQuery}`,
  contractDemandDetail: (id = ':id', tabSlug?: string): string =>
    joinPath([RouteIdPart.demand, RouteIdPart.contractDemand, id, tabSlug]),
  coverageAssistant: `/${RouteIdPart.demand}/${RouteIdPart.contractDemand}/${RouteIdPart.coverageAssistant}`,
  sourcingCaseDetail: (id = ':id', tabSlug?: string): string =>
    joinPath([RouteIdPart.demand, RouteIdPart.contractDemand, RouteIdPart.sourcingCase, id, tabSlug]),
  dataFoundation: `/${RouteIdPart.dataFoundation}`,
  savings: `/${RouteIdPart.dataFoundation}/${RouteIdPart.savings}`,
  savingDetail: (id = ':id'): string => `/${RouteIdPart.dataFoundation}/${RouteIdPart.savings}/${id}`,
  negotiationDemandDetail: (id = ':id', tabSlug?: string): string => joinPath([RouteIdPart.demand, id, tabSlug]),
  demandDetail: (id = ':id', tabSlug?: string): string => joinPath([RouteIdPart.demand, id, tabSlug]),
  demo: `/${RouteIdPart.demo}`,
  home: `/${RouteIdPart.home}`,
  login: `/${RouteIdPart.login}`,
  authCallback: `/${RouteIdPart.auth}/${RouteIdPart.callback}`,
  loginTeamsStart: `/${RouteIdPart.teamsAuth}/${RouteIdPart.start}`,
  loginTeamsEnd: `/${RouteIdPart.teamsAuth}/${RouteIdPart.end}`,
  claraNegotiation: (domain?: NegotiationDomain): string => {
    return domain ? `/${RouteIdPart.negotiation}/${kebabCase(domain)}` : `/${RouteIdPart.negotiation}`;
  },
  negotiationCases: `/${RouteIdPart.demand}/${RouteIdPart.demandNegotiationCase}`,
  negotiationCreate: `/${RouteIdPart.demand}/${RouteIdPart.demandNegotiationCase}/${RouteIdPart.create}`,
  claraNegotiationCreate: `/${RouteIdPart.negotiation}/${RouteIdPart.negotiationCase}/${RouteIdPart.create}`,
  negotiationDetail: (id = ':id'): string => `/${RouteIdPart.demand}/${RouteIdPart.demandNegotiationCase}/${id}`,
  negotiationUpload: `/${RouteIdPart.negotiation}/${RouteIdPart.negotiationCase}/${RouteIdPart.upload}`,
  notifications: `/${RouteIdPart.notifications}`,
  orderDetail: (id = ':id'): string =>
    `/${RouteIdPart.analytics}/${RouteIdPart.base}/${kebabCase(BaseDomainType.ORDER)}/${id}`,
  purchaseRequisitionDetail: (id = ':id'): string => {
    return `/${RouteIdPart.analytics}/${RouteIdPart.base}/${kebabCase(BaseDomainType.PURCHASE_REQUISITION)}/${id}`;
  },
  root: `/`,
  cockpit: `/${RouteIdPart.cockpit}`,
  cockpitConversation: (id = ':id'): string => `/${RouteIdPart.cockpit}/${RouteIdPart.conversation}/${id}`,
  cockpitConversationEntity: (entityVariant = ':entityVariant', id = ':id'): string =>
    `/${RouteIdPart.cockpit}/${RouteIdPart.conversation}/${entityVariant}/${id}`,
  cockpitGuided: (id = ':id'): string => `/${RouteIdPart.cockpit}/${RouteIdPart.guided}/${id}`,
  search: `/${RouteIdPart.search}`,
  searchDetail: (searchDomain = ':searchDomain'): string => `/${RouteIdPart.search}/${kebabCase(searchDomain)}`,
  settings: `/${RouteIdPart.settings}`,
  ruleList: `/${RouteIdPart.settings}/${RouteIdPart.rule}`,
  ruleCreate: `/${RouteIdPart.settings}/${RouteIdPart.rule}/${RouteIdPart.create}`,
  ruleDetail: (id = ':ruleId'): string => `/${RouteIdPart.settings}/${RouteIdPart.rule}/${id}`,
  ruleEvaluationLogDetail: (id = ':id', logId = ':logId'): string =>
    `/${RouteIdPart.settings}/${RouteIdPart.rule}/${id}/${RouteIdPart.log}/${logId}`,
  knowledgeRepository: `/${RouteIdPart.settings}/${RouteIdPart.knowledgeRepository}`,
  teamManagement: `/${RouteIdPart.teamManagement}`,
  supplierDetail: (id = ':id', tabSlug?: string): string =>
    joinPath([RouteIdPart.analytics, RouteIdPart.base, kebabCase(BaseDomainType.SUPPLIER), id, tabSlug]),
  support: `/${RouteIdPart.support}`,
  intake: `/${RouteIdPart.intake}`,
  intakeDetail: (id = ':id'): string => `/${RouteIdPart.intake}/${id}`,
  smartLinksDetail: (id = ':id'): string => `${RouteIdPart.smartLinks}/${id}`,
  riskSustainability: `/${RouteIdPart.riskSustainability}`,
  riskDetail: (id = ':id'): string => `/${RouteIdPart.riskSustainability}/risks/${id}`,
  riskCountryReportDetail: (id = ':id'): string => `/${RouteIdPart.riskSustainability}/country-report/${id}`,
  internalTools: `/${RouteIdPart.internalTools}`,
  rule: `/${RouteIdPart.rule}`,
  popupCallback: `/${RouteIdPart.auth}/${RouteIdPart.popupCallback}`,
};
